import { formatNumber } from '@trmediaab/zebra-utils';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import Toggle from 'react-toggle';

import {
  Details,
  Helper,
  PresetReductionDefaults,
  RacesLength,
  StartNrRange,
} from 'atg/constants/PresetReduction';
import { bet as betSelectors } from 'atg/redux/selectors';
import Button from 'shared/components/button/Button';
import Question from 'shared/components/icon/Question';
import Icon from 'shared/components/icon/Undo';
import NumberInput from 'shared/components/input/NumberInput';
import ContentWrapper from 'shared/components/layout/ContentWrapper';
import Slider from 'shared/components/Slider';
import { showHelpDialog } from 'shared/redux/actions';
import { formatDateTime } from 'shared/utils/dateUtils';
import { getText } from 'shared/utils/Text';

import styles from './index.module.scss';

const SliderComponent = ({
  betType,
  data,
  description,
  from,
  onUpdate,
  title,
  to,
}) => (
  <Slider
    className={styles.slider}
    description={description != null ? getText(description) : undefined}
    disabled={data.disabled}
    max={RacesLength[betType]}
    onChange={([newFrom, newTo]) =>
      onUpdate({
        [from]: newFrom,
        [to]: newTo,
      })
    }
    title={title != null ? getText(title) : undefined}
    values={[data[from], data[to]]}
  />
);

const PresetReduction = ({
  betType,
  isLoading,
  presetBetBasedAt,
  presetReduction,

  showAlertWhenLocked,
  onPresetReductionUpdate,
  showHelpDialog,
}) => {
  const defaults = useMemo(
    () => ({
      disabled: true,
      ...PresetReductionDefaults[betType],
    }),
    [betType],
  );

  const [data, setData] = useState(presetReduction || defaults);

  useEffect(() => {
    presetReduction != null && setData(presetReduction);
  }, [presetReduction]);

  const onStateUpdate = useCallback(
    updateData => {
      if (showAlertWhenLocked()) {
        return;
      }
      const newData = {
        ...data,
        ...updateData,
      };

      if (typeof updateData.update !== 'boolean') {
        delete newData.update;
      }

      setData(newData);
      onPresetReductionUpdate(newData);
    },
    [data, onPresetReductionUpdate, showAlertWhenLocked],
  );

  const onResetClick = useCallback(
    () => onStateUpdate({ ...defaults, disabled: data.disabled }),
    [data.disabled, defaults, onStateUpdate],
  );

  return (
    <div className={styles.wrapper}>
      <ContentWrapper columnWidth="narrow" className={styles.container}>
        <div className={styles.headerContainer}>
          <div className={styles.header}>
            <Button
              className={styles.aboutLink}
              onClick={() => showHelpDialog(Helper)}
            >
              <Question className={styles.icon} />
              <span className={styles.desktop}>
                {getText('preset-reduction-about-desktop')}
              </span>
              <span className={styles.mobile}>
                {getText('preset-reduction-about-mobile')}
              </span>
            </Button>

            <div>{getText('preset-reduction-header')}</div>

            <Toggle
              checked={!data.disabled}
              className={styles.toggle}
              disabled={isLoading}
              icons={false}
              onChange={() =>
                onStateUpdate({
                  disabled: !data.disabled,
                })
              }
            />

            <div className={styles.headerExplain}>
              {getText('preset-reduction-toggle')}
            </div>
          </div>

          {presetBetBasedAt && (
            <div className={styles.subHeader}>
              {getText(
                'preset-reduction-sub-header',
                formatDateTime(Number.parseInt(presetBetBasedAt, 10)),
              )}
            </div>
          )}

          {!data.disabled && (
            <Button
              className={styles.headerBtn}
              disabled={isLoading}
              onClick={() => {
                onStateUpdate({
                  update: true,
                });
              }}
            >
              {getText('button-update')}
            </Button>
          )}

          <div className={styles.explain}>
            {getText(
              'preset-reduction-explain',
              Details.percent[betType],
              betType,
              formatNumber(Details.median[betType]),
              formatNumber(Details.average[betType]),
            )}
          </div>
        </div>

        <SliderComponent
          betType={betType}
          data={data}
          description="preset-reduction-fav-win-details"
          from="minFavoriteWin"
          onUpdate={onStateUpdate}
          title="preset-reduction-fav-win"
          to="maxFavoriteWin"
        />

        {betType === 'V86' && (
          <SliderComponent
            betType={betType}
            data={data}
            description="preset-reduction-second-fav-win-details"
            from="minSecondFavoriteWin"
            onUpdate={onStateUpdate}
            title="preset-reduction-second-fav-win"
            to="maxSecondFavoriteWin"
          />
        )}

        <SliderComponent
          betType={betType}
          data={data}
          description="preset-reduction-upset-win-details"
          from="minUpsetWin"
          onUpdate={onStateUpdate}
          title="preset-reduction-upset-win"
          to="maxUpsetWin"
        />

        <SliderComponent
          betType={betType}
          data={data}
          description="preset-reduction-upset-major-win-details"
          from="minMajorUpsetWin"
          onUpdate={onStateUpdate}
          title="preset-reduction-upset-major-win"
          to="maxMajorUpsetWin"
        />

        <div className={styles.caption}>
          {getText('preset-reduction-start-number')}
        </div>

        <div className={styles.chooseNumberDetails}>
          {getText('preset-reduction-start-number-details')}
        </div>

        <div className={styles.chooseNumber}>
          <div className={styles.chooseNumberItem}>
            <div className={styles.chooseNumberItemHdr}>Min.</div>
            <NumberInput
              disabled={data.disabled}
              allowEmpty={false}
              value={data.minStartNummerSum}
              maxValue={data.maxStartNummerSum || StartNrRange.max}
              onChange={value => {
                onStateUpdate({
                  minStartNummerSum: Number.parseInt(value, 10),
                });
              }}
            />
          </div>
          <div className={styles.chooseNumberItem}>
            <div className={styles.chooseNumberItemHdr}>Max.</div>
            <NumberInput
              disabled={data.disabled}
              allowEmpty={false}
              value={data.maxStartNummerSum}
              minValue={data.minStartNummerSum}
              maxValue={StartNrRange.max}
              onChange={value => {
                onStateUpdate({
                  maxStartNummerSum: Number.parseInt(value, 10),
                });
              }}
            />
          </div>
        </div>

        <SliderComponent
          betType={betType}
          data={data}
          description="preset-reduction-top-5-jockeys-details"
          from="minTop5Jockeys"
          onUpdate={onStateUpdate}
          title="preset-reduction-top-5-jockeys"
          to="maxTop5Jockeys"
        />

        <SliderComponent
          betType={betType}
          data={data}
          description="preset-reduction-bottom-10-jockeys-details"
          from="minBottom10Jockeys"
          onUpdate={onStateUpdate}
          title="preset-reduction-bottom-10-jockeys"
          to="maxBottom10Jockeys"
        />

        <Button
          className={styles.saveBtn}
          disabled={
            isLoading || JSON.stringify(data) === JSON.stringify(defaults)
          }
          onClick={onResetClick}
        >
          <Icon className={styles.icon} />
          {getText('preset-reduction-btn-save')}
        </Button>
      </ContentWrapper>
    </div>
  );
};

PresetReduction.propTypes = {
  betType: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  presetBetBasedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  presetReduction: PropTypes.object,

  showAlertWhenLocked: PropTypes.func.isRequired,
  onPresetReductionUpdate: PropTypes.func.isRequired,
  showHelpDialog: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  presetBetBasedAt: betSelectors.getPresetBetBasedAt(state),
});

const mapDispatchToProps = dispatch => ({
  showHelpDialog: ({ header, body }) => {
    dispatch(showHelpDialog(header, body));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PresetReduction);
